import React, { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  RefreshCw,
  User,
  Mail,
  Shield,
  House,
  Calendar,
} from "lucide-react";
import api, { ApiResponse, getRoles } from "../../services/api";
import { toast } from "react-hot-toast";
import Cookies from "js-cookie";
import { Hotel, Role, Employee, RegisterResponse } from "../../utils";

const EmployeeManagement: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [hotels, setHotels] = useState<Hotel[]>([]);

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    passcode: "",
    role: "",
    restaurantId: "",
  });
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  const token = Cookies.get("token");

  useEffect(() => {
    fetchEmployees();
    fetchRoles();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<ApiResponse<Employee[]>>("/new");
      if (response.data.success) {
        setEmployees(response.data.data);
      } else {
        setEmployees([]);
        setError("Failed to load employees");
      }
    } catch (error) {
      setError("Failed to load employees. Please try again.");
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const { data } = await getRoles();
      if (data.success) {
        setRoles(data.data);
      }
    } catch (err) {
      setError("Failed to fetch roles");
    }
  };

  const fetchHotels = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<ApiResponse<Hotel[]>>(
        "/admin/getHotelInformation",
      );
      setHotels(res.data.data);
    } catch (err) {
      setError("Failed to load hotels. Please try again.");
      setHotels([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHotels();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    setError(null);

    if (!formData.role) {
      toast.error("Please select a role");
      setFormLoading(false);
      return;
    }
    if (!formData.restaurantId) {
      toast.error("Please Select a location");
      setFormLoading(false);
      return;
    }

    try {
      if (editingEmployee) {
        // Update employee
        const payload: {
          name?: string;
          email?: string;
          password?: string;
          passcode?: string;
          restaurantId?: string;
        } = {};
        if (formData.name.trim()) payload.name = formData.name.trim();
        if (formData.email.trim()) payload.email = formData.email.trim();
        if (formData.password.trim())
          payload.password = formData.password.trim();
        if (formData.passcode.trim() !== "" || editingEmployee.passcode !== undefined) {
          payload.passcode = formData.passcode.trim();
        }
        if (formData.restaurantId) payload.restaurantId = formData.restaurantId;

        await api.put(`/new/${editingEmployee._id}`, payload);
        toast.success("Employee updated successfully!");
      } else {
        // Register new employee
        const { data } = await api.post<RegisterResponse>(
          "/new/register",
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (data.success) {
          toast.success("Employee registered successfully!");
        } else {
          toast.error(data.message || "Registration failed");
          setFormLoading(false);
          return;
        }
      }

      resetForm();
      setShowModal(false);
      fetchEmployees();
    } catch (error: any) {
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error(
          editingEmployee
            ? "Update failed. Please try again."
            : "Registration failed. Please try again.",
        );
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (email: string) => {
    if (!window.confirm("Are you sure you want to delete this employee?"))
      return;

    try {
      setError(null);
      await api.delete(`/new/${email}`);
      fetchEmployees();
      toast.success("Employee deleted successfully!");
    } catch (error) {
      setError("Failed to delete employee. Please try again.");
      toast.error("Failed to delete employee.");
    }
  };

  const handleEdit = (employee: Employee) => {
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      email: employee.email,
      password: "",
      passcode: employee.passcode || "",
      role: employee.role,
      restaurantId: employee.restaurantId?._id || "",
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingEmployee(null);
    setFormData({
      name: "",
      email: "",
      password: "",
      passcode: "",
      role: "",
      restaurantId: "",
    });
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  // Filter and pagination logic
  const filteredEmployees = employees.filter(
    (employee) =>
      employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.role.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedEmployees = filteredEmployees.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="relative max-w-7xl mx-auto p-4 pt-0 lg:p-8 overflow-y-auto no-scrollbar flex flex-col h-screen">
      {/* ================= HEADER ================= */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm   py-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Employees</h1>
          <button
            onClick={openAddModal}
            className="w-auto self-start sm:self-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition inline-flex items-center gap-2 whitespace-nowrap">
            <Plus size={18} />
            Add Employee
          </button>
        </div>

        {/* ================= SEARCH ================= */}
        <div className=" rounded-lg mb-6 ">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Search Employees
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by name, email, or role..."
                className="pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={fetchEmployees}
              className="h-10 w-10 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition flex items-center justify-center"
              title="Refresh Employees"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </div>
      {/* ================= ERROR ================= */}
      {error && (
        <div className="bg-red-500 text-white p-3 rounded-md mb-4 text-center">
          {error}
        </div>
      )}

      {/* ================= CONTENT ================= */}
      <div className="flex-1">
        {paginatedEmployees.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <p className="text-lg font-medium">No employees found</p>
              <p className="text-sm mt-1">
                {searchTerm
                  ? "Try searching with a different term"
                  : "Add your first employee to get started"}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {paginatedEmployees.map((employee) => (
              <div
                key={employee._id}
                className="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                {/* Employee Header */}
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <User size={16} className="text-blue-600" />
                      <h3 className="text-xl font-bold text-gray-800 mb-0">
                        {employee.name}
                      </h3>
                    </div>

                    <div className="flex items-center gap-2 mb-1">
                      <Mail size={14} className="text-gray-400" />
                      <p className="text-sm text-gray-600 mb-0">
                        {employee.email}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 mb-2">
                      <Shield size={14} className="text-gray-400" />
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {employee.role}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 mb-2">
                      <House size={14} className="text-gray-400" />
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {employee.restaurantId
                          ? employee.restaurantId.hotelName
                          : "No location assigned"}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Calendar size={14} className="text-gray-400" />
                      <p className="text-xs text-gray-500 mb-0">
                        Created:{" "}
                        {new Date(employee.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(employee)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition"
                  >
                    <Edit size={14} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(employee.email)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition"
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ================= PAGINATION ================= */}
      {filteredEmployees.length > 0 && totalPages > 1 && (
        <div className="py-4 flex justify-center ">
          <div className="flex gap-2 items-center">
            {/* Previous Button */}
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Prev
            </button>

            {/* Page Numbers */}
            {(() => {
              const pages = [];
              const showEllipsis = totalPages > 7;

              if (!showEllipsis) {
                // Show all pages if 7 or fewer
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === i
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {i}
                    </button>,
                  );
                }
              } else {
                // Smart pagination for many pages
                const startPage = Math.max(1, currentPage - 2);
                const endPage = Math.min(totalPages, currentPage + 2);

                // Always show first page
                if (startPage > 1) {
                  pages.push(
                    <button
                      key={1}
                      onClick={() => setCurrentPage(1)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === 1
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      1
                    </button>,
                  );

                  if (startPage > 2) {
                    pages.push(
                      <span key="ellipsis1" className="px-2 py-1 text-gray-500">
                        ...
                      </span>,
                    );
                  }
                }

                // Show pages around current page
                for (let i = startPage; i <= endPage; i++) {
                  if (i !== 1 && i !== totalPages) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-3 py-1 rounded transition ${
                          currentPage === i
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 hover:bg-gray-300"
                        }`}
                      >
                        {i}
                      </button>,
                    );
                  }
                }

                // Always show last page
                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) {
                    pages.push(
                      <span key="ellipsis2" className="px-2 py-1 text-gray-500">
                        ...
                      </span>,
                    );
                  }

                  pages.push(
                    <button
                      key={totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === totalPages
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {totalPages}
                    </button>,
                  );
                }
              }

              return pages;
            })()}

            {/* Next Button */}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ================= MODAL ================= */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ top: "0", left: "0", right: "0", bottom: "0" }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setShowModal(false)}
          />

          {/* Modal Container */}
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col mx-auto">
            {/* Modal Header - Fixed */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg flex justify-between items-center flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-800 mb-0">
                {editingEmployee ? "Edit Employee" : "Add New Employee"}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto">
              <form id="employee-form" onSubmit={handleSubmit} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="Enter employee name"
                      required
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      placeholder="Enter employee email"
                      required
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Role *
                    </label>
                    <select
                      value={formData.role}
                      onChange={(e) =>
                        setFormData({ ...formData, role: e.target.value })
                      }
                      required
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                    >
                      <option value="">Select a role</option>
                      {roles.map((role) => (
                        <option key={role._id} value={role.name}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Location *
                    </label>
                    <select
                      value={formData.restaurantId}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          restaurantId: e.target.value,
                        })
                      }
                      required
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                    >
                      <option value="">Select a location</option>
                      {hotels.map((hotel) => (
                        <option key={hotel._id} value={hotel._id}>
                          {hotel.hotelName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Password{" "}
                      {editingEmployee ? "(leave blank to keep current)" : "*"}
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      placeholder={
                        editingEmployee
                          ? "Enter new password (optional)"
                          : "Enter password"
                      }
                      required={!editingEmployee}
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Kitchen Passcode{" "}
                      <span className="text-gray-400 font-normal">(6-digit PIN for cancel authorization)</span>
                    </label>
                    <input
                      type="password"
                      inputMode="numeric"
                      pattern="\d{6}"
                      maxLength={6}
                      value={formData.passcode}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                        setFormData({ ...formData, passcode: val });
                      }}
                      placeholder="e.g. 123456"
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all tracking-widest font-mono"
                    />
                    {editingEmployee && (
                      <p className="text-xs text-gray-400 mt-1">Leave blank to remove passcode</p>
                    )}
                  </div>
                </div>
              </form>
            </div>

            {/* Modal Footer - Fixed */}
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-lg flex justify-end gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-3 py-2 sm:px-5 sm:py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="employee-form"
                className="px-3 py-2 sm:px-5 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                disabled={formLoading}
              >
                {formLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin">⏳</span>
                    {editingEmployee ? "Updating..." : "Registering..."}
                  </span>
                ) : editingEmployee ? (
                  "Update Employee"
                ) : (
                  "Register Employee"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagement;
